import {
	createErrorInstance,
	createHeaders,
	fetchFileRequest,
	makeRequest,
	uploadFileRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	ChangeDealDto,
	CreateGdwtCustomDetailsDefinitionsDto,
	CreateManyCustomDetailsDefinitionsForPreFillableFormTemplateDto,
	CreatePreFillableFormTemplateDto,
	DataCustomDetailTct,
	DataCustomDetailWithoutFormTct,
	DataDealTct,
	DataFileTct,
	DataGeneratableDocumentTemplateWithAllRelationsTct,
	DataGeneratableDocumentWorkflowTemplateFieldTct,
	DataGeneratableDocumentWorkflowTemplateTct,
	DataIsAnyInvestorActiveInMIPTct,
	DataIsAnyInvitedInvestorInMIPTct,
	DataLoanDefinitionWithLoansTct,
	DataMipTct,
	DataPreFillableFormCustomDetailDefinitionTct,
	DataPreFillableFormTemplateWithAllRelationsTct,
	PreFillableFormOrganizationOptionRes,
	DataPreFillableFormTemplateWithProjectsTct,
	DataSimpleInvestorTct,
	DataSimpleMipTct,
	DataUserTct,
	DeleteCustomDetailsDefinitionsDto,
	GdwtCustomDetailDefinitionTct,
	GeneratableDocumentWorkflowTemplateType,
	GetGeneratableDocumentsTemplateAssignedProjectRes,
	GetInvestorPersonalInformationRes,
	GetInvestorShareAssociationsDataRes,
	InvestorWithInvestmentWorkflowRes,
	PreFillableFormTemplateCustomDetailsDefinitionsRes,
	DealBasicDataTct,
	SummaryDealTct,
	UpdateCustomDetailsDefinitionsDto,
	UpdateCustomDetailTypeDto,
	UpdateGdwtCustomDetailDefinitionsDto,
	UpdateGeneratableDocumentTemplateDto,
	UpdateGeneratableDocumentTemplateSmartFieldsDto,
	UpdateInvestorPersonalDetailsDto,
	UpdateMipIsKycRequiredDto,
	UpdateMipIsLockedDto,
	UpdatePreFillableFormTemplateDto,
	UpdateShareAssociationsDto,
	UpdateUserEmailDto,
	UpdateUserNameDto,
	UploadPreFillableFormTemplateFileRes,
	UserSignatureRes,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { apiClient } from '@dealstack/frontend/api/client';
import {
	FilePreviewMode,
	GeneratableDocumentVersion,
} from '@dealstack/shared/lib/common/business/generatableDocuments';
import { EmailChangeEligibilityView } from '@dealstack/shared/lib/common/emailChangeEligibility';
import { AssignProjectToGeneratableDocumentTemplateDto } from '@dealstack/shared/lib/generated/models/AssignProjectToGeneratableDocumentTemplateDto.ts';
import { UnassignProjectFromGeneratableDocumentTemplateDto } from '@dealstack/shared/lib/generated/models/UnassignProjectFromGeneratableDocumentTemplateDto.ts';
import { CreateDocumentWorkflowFileRes } from '@dealstack/shared/lib/generated/models/CreateDocumentWorkflowFileRes';
import { CreateDocumentWorkflowRes } from '@dealstack/shared/lib/generated/models/CreateDocumentWorkflowRes';
import { CreateDocumentWorkflowsDto } from '@dealstack/shared/lib/generated/models/CreateDocumentWorkflowsDto';
import { DocumentWorkflowFileDto } from '@dealstack/shared/lib/generated/models/DocumentWorkflowFileDto';

export interface IInviteUserParams {
	email: string;
}

export const getActiveMips = async (
	endpoint: string,
	args: {
		searchQuery: string;
		includeDeleted: boolean;
		includeDraft: boolean;
	}
) => {
	const { searchQuery, includeDeleted, includeDraft } = args;
	const response = await makeRequest<ApiV1.Response<DataSimpleMipTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				searchQuery,
				includeDeleted,
				includeDraft,
			}),
		},
		{}
	);
	return response;
};

export const getAllUsers = async (
	endpoint: string,
	args: {
		searchQuery: string;
		searchLimit: string;
	}
) => {
	const { searchQuery, searchLimit } = args;
	const response = await makeRequest<ApiV1.Response<DataUserTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: {
				searchQuery,
				limit: searchLimit,
			},
		},
		{}
	);
	return response;
};

export const postReassignInvestor = async (
	{ investorId, targetUserId }: { investorId: string; targetUserId: string },
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<DataUserTct[]>>(
		{
			endpoint: API_PATHS.GODMODE_REASIGNINVESTOR_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ investorId, targetUserId }),
		},
		{ ...options }
	);
	return response;
};

export const getUserData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataUserTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
	return response;
};

export interface IUserEmailChangeEligibilityParams {
	userId: string;
	targetEmail?: string;
}

export const getUserEmailChangeEligibility = async (
	{ userId, targetEmail }: IUserEmailChangeEligibilityParams,
	signal?: AbortSignal
): Promise<ApiV1.Response<EmailChangeEligibilityView>> => {
	return await makeRequest<ApiV1.Response<EmailChangeEligibilityView>>(
		{
			endpoint: formatUnicorn(API_PATHS.GODMODE_USER_USERID_EMAIL_CHANGE_ELIGIBILITY_GET, {
				userId,
			}),
			method: HTTP_METHOD.GET,
			queryString: targetEmail ? { targetEmail } : undefined,
		},
		{ abortSignal: signal }
	);
};

export const deleteUserSoft = async (userId: string) => {
	const response = await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_USER_USERID_SOFT_DELETE, { userId }),
		method: HTTP_METHOD.DELETE,
	});
	return response;
};

type SoftDeleteInvestmentWorkflowRequestArgs = {
	investmentWorkflowId: string;
};

export const softDeleteInvestmentWorkflow = async ({
	investmentWorkflowId,
}: SoftDeleteInvestmentWorkflowRequestArgs) => {
	const response = await makeRequest<ApiV1.Response<DataUserTct[]>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.GODMODE_SOFT_DELETE_INVESTMENTWORKFLOW_INVESTMENTWORKFLOWID_POST,
				{ investmentWorkflowId }
			),
			method: HTTP_METHOD.POST,
		},
		{}
	);
	return response;
};

export const updateUserName = async ({
	userId,
	body,
}: {
	userId: string;
	body: UpdateUserNameDto;
}) => {
	return await makeRequest<ApiV1.Response<null>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_UPDATE_USER_NAME_USERID_PATCH, { userId }),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const updateUserEmail = async ({
	userId,
	body,
}: {
	userId: string;
	body: UpdateUserEmailDto;
}) => {
	return await makeRequest<ApiV1.Response<null>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_UPDATE_USER_EMAIL_USERID_PATCH, { userId }),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const updateMipKycRequired = async ({
	mipId,
	body,
}: {
	mipId: string;
	body: UpdateMipIsKycRequiredDto;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_KYC_REQUIRED_PATCH, {
			mipId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const updateMipIsLocked = async ({
	mipId,
	body,
}: {
	mipId: string;
	body: UpdateMipIsLockedDto;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_LOCKED_PATCH, {
			mipId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export interface IRevokeInvestmentWorkflowRequestArgs {
	revokeInvestmentWorkflowId: string;
}

export const revokeInvestmentWorkflow = async ({
	revokeInvestmentWorkflowId,
}: IRevokeInvestmentWorkflowRequestArgs) => {
	const response = await makeRequest<ApiV1.Response<DataUserTct[]>>(
		{
			endpoint: formatUnicorn('/godmode/revoke/{investmentWorkflowId}', {
				investmentWorkflowId: revokeInvestmentWorkflowId,
			}),
			method: HTTP_METHOD.POST,
		},
		{}
	);
	return response;
};

export const deleteKycFile = async ({ kycFileId }: { kycFileId: string }) => {
	const response = await makeRequest<ApiV1.Response<void>>(
		{
			endpoint: formatUnicorn(API_PATHS.GODMODE_KYC_FILE_KYCFILEID_DELETE, { kycFileId }),
			method: HTTP_METHOD.DELETE,
		},
		{}
	);
	return response;
};

export const removeInvestorFromMip = async ({ investorId }: { investorId: string }) => {
	return makeRequest<ApiV1.Response<DataUserTct[]>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_REMOVE_INVESTOR_INVESTORID_POST, { investorId }),
		method: HTTP_METHOD.POST,
	});
};

export const getFileInfo = async (endpoint: string) => {
	const response = await makeRequest<
		ApiV1.Response<{
			id: string;
			name: string;
			mimeType: string;
			extension: string;
			deleted: boolean;
			updatedAt: Date;
			createdAt: Date;
		}>
	>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{}
	);
	return response;
};

export const getAllInvestorData = async (endpoint: string, signal?: AbortSignal) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const response = await makeRequest<ApiV1.Response<Record<string, any>>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getAllInvestorsForMIPData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<{ investors: DataSimpleInvestorTct[] }>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export interface DataMipWihtInvestors extends DataMipTct {
	investors: DataSimpleInvestorTct[];
}
export const getMipInvestmentsData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataMipWihtInvestors>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getMipData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataSimpleMipTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const unarchiveDealDraftMips = async ({ dealId }: { dealId: string }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_DEAL_DEALID_DEAL_DRAFT_MIPS_PUT, {
			dealId,
		}),
		method: HTTP_METHOD.PUT,
	});
};

export const deleteDealMipsAndInvestors = async ({ dealId }: { dealId: string }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_DEAL_DEALID_DEAL_MIPS_INVESTORS_DELETE, {
			dealId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const updateMipInvestorsDeleted = async ({ mipId }: { mipId: string }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_MIP_INVESTORS_DELETE, {
			mipId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const isAnyActiveInvestorInMIP = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataIsAnyInvestorActiveInMIPTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const isAnyInvitedInvestorInMIP = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataIsAnyInvitedInvestorInMIPTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getMipCustomDetailsWithoutFormData = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	const response = await makeRequest<ApiV1.Response<DataCustomDetailWithoutFormTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const bulkCreateCustomDetails =
	(mipId: string) =>
	async ({
		formData,
		onUploadProgress,
	}: {
		formData: FormData;
		onUploadProgress?: (percent: number) => void;
	}) => {
		const response = await uploadFileRequest<ApiV1.Response<DataFileTct>>(
			{
				endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_CUSTOM_DETAILS_POST, {
					mipId,
				}),
				method: HTTP_METHOD.POST,
			},
			formData,
			onUploadProgress
		);
		return response;
	};

export const getDealData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<SummaryDealTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getDealsListData = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<SummaryDealTct[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const getAllNotDeletedProjects = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<DealBasicDataTct[]>>(
		{ endpoint, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	).then((res) => res.payload);
};

export const changeDeal = async ({ mipId, body }: { mipId: string; body: ChangeDealDto }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_DEALS_MIPID_PATCH, {
			mipId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const updateCustomDetailType = async ({
	customDetailId,
	body,
}: {
	customDetailId: string;
	body: UpdateCustomDetailTypeDto;
}) => {
	return await makeRequest<ApiV1.Response<DataCustomDetailTct[]>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_UPDATE_CUSTOM_DETAIL_TYPE_CUSTOMDETAILID_PATCH, {
			customDetailId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(body),
	});
};

export const getInvestmentWorkflowByTsaId = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<GetInvestorShareAssociationsDataRes[]>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const updateInvestmentWorkflow = async (
	updatedShareAssociations: UpdateShareAssociationsDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_SHARE_ASSOCIATION_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(updatedShareAssociations),
	});
};
export const getFileReactQuery = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const headers = await createHeaders({ endpoint: '/file', method: HTTP_METHOD.GET });
	const config = getGlobalConfig();
	const url = config.apiUrl;

	try {
		const response = await apiClient(
			`${url}${formatUnicorn(endpoint, {
				id: args.id,
			})}`,
			{
				headers,
				redirect: 'follow',
				signal,
			}
		);
		if (!response.body) {
			throw Error('Failed to fork streams');
		}
		const [stream, streamCopy] = response.body.tee();

		const reader = streamCopy.getReader();

		// eslint-disable-next-line no-constant-condition
		while (true) {
			const { done } = await reader.read();
			if (done) {
				const finalResponse = new Response(stream);
				for (const [key, value] of response.headers.entries()) {
					finalResponse.headers.set(key, value);
				}
				return finalResponse;
			}
		}
	} catch (error) {
		const errorInstance = await createErrorInstance(error);
		throw errorInstance;
	}
};

export enum ReplaceFile {
	DEFAULT = 'DEFAULT',
	WET_INK_THROUGH_CONTRACT_FILE = 'WET_INK_THROUGH_CONTRACT_FILE',
}

export const replaceFile = async ({
	fileId,
	formData,
	onUploadProgress,
	mode,
}: {
	fileId: string;
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
	mode?: ReplaceFile;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<DataFileTct>>(
		{
			endpoint: formatUnicorn(`/godmode/file/{fileId}/replace?mode={mode}`, {
				fileId,
				mode: mode ?? ReplaceFile.DEFAULT,
			}),
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

export const bulkEditMipInvestmentsUploadFile =
	(mipId: string) =>
	async ({
		formData,
		onUploadProgress,
	}: {
		formData: FormData;
		onUploadProgress?: (percent: number) => void;
	}) => {
		const response = await uploadFileRequest<ApiV1.Response<string[]>>(
			{
				endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_EDIT_BULK_POST, {
					mipId,
				}),
				method: HTTP_METHOD.POST,
			},
			formData,
			onUploadProgress
		);
		return response;
	};

export const getInvestorPersonalDetailsByUserId = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	const response = await makeRequest<ApiV1.Response<GetInvestorPersonalInformationRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};
interface IUpdateInvestorPersonalDetails extends UpdateInvestorPersonalDetailsDto {
	userId: string;
}
export const updateInvestorPersonalDetails = async ({
	userId,
	...updatedData
}: IUpdateInvestorPersonalDetails) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_INVESTOR_USERID_PUT, { userId }),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(updatedData),
	});
};

export const removeInvestorPersonalDetails = async ({ userId }: { userId: string }) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_INVESTOR_PERSONAL_DETAILS_USERID_DELETE, {
			userId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const getDealWithActiveMips = async (endpoint: string, signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<DataDealTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);
	return response;
};

export const deleteDraftIP = async ({
	mipId,
	withEntityDelete,
}: {
	mipId: string;
	withEntityDelete?: boolean;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_MIP_INVESTORS_DELETE, { mipId }),
		queryString: {
			isDraft: true,
			withEntityDelete,
		},
		method: HTTP_METHOD.DELETE,
	});
};

export const uploadFile = async ({
	formData,
	onUploadProgress,
}: {
	formData: FormData;
	onUploadProgress?: (percent: number) => void;
}) => {
	const response = await uploadFileRequest<ApiV1.Response<DataFileTct>>(
		{
			endpoint: API_PATHS.GODMODE_FILE_POST,
			method: HTTP_METHOD.POST,
		},
		formData,
		onUploadProgress
	);
	return response;
};

export const getInvestorsWithTsaData = async (
	endpoint: string,
	args: { mipId: string; search: string }
) => {
	const { search, mipId } = args;
	const response = await makeRequest<ApiV1.Response<InvestorWithInvestmentWorkflowRes[]>>(
		{
			endpoint: formatUnicorn(endpoint, { mipId }),
			method: HTTP_METHOD.GET,
			queryString: {
				search,
			},
		},
		{}
	);
	return response;
};

export const getMipLoansData = async (
	endpoint: string,
	args: { mipId: string },
	signal?: AbortSignal
) => {
	const { mipId } = args;

	const response = await makeRequest<ApiV1.Response<DataLoanDefinitionWithLoansTct[]>>(
		{
			endpoint: formatUnicorn(endpoint, { mipId }),
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);

	return response;
};

export const bulkCreateMipILoansUploadFile =
	(mipId: string) =>
	async ({
		formData,
		onUploadProgress,
	}: {
		formData: FormData;
		onUploadProgress?: (percent: number) => void;
	}) => {
		const response = await uploadFileRequest<ApiV1.Response<DataFileTct>>(
			{
				endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_LOANS_EDIT_BULK_POST, {
					mipId,
				}),
				method: HTTP_METHOD.POST,
			},
			formData,
			onUploadProgress
		);
		return response;
	};

export const bulkCreateNewTSAUploadFile =
	(mipId: string) =>
	async ({
		formData,
		onUploadProgress,
	}: {
		formData: FormData;
		onUploadProgress?: (percent: number) => void;
	}) => {
		const response = await uploadFileRequest<ApiV1.Response<string[]>>(
			{
				endpoint: formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_BULK_CREATE_POST, {
					mipId,
				}),
				method: HTTP_METHOD.POST,
			},
			formData,
			onUploadProgress
		);
		return response;
	};

export const uploadPreFillableFormTemplateFile = async (file: File) => {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('fileSize', file.size.toString());

	return await uploadFileRequest<ApiV1.Response<UploadPreFillableFormTemplateFileRes>>(
		{
			endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_UPLOAD_FILE_POST,
			method: HTTP_METHOD.POST,
		},
		formData
	);
};

export const createPreFillableFormTemplate = async (data: CreatePreFillableFormTemplateDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CREATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const createManyCustomDetailDefinitionForPreFillableFormTemplate = async (
	data: CreateManyCustomDetailsDefinitionsForPreFillableFormTemplateDto
) => {
	return await makeRequest<ApiV1.Response<DataPreFillableFormCustomDetailDefinitionTct>>({
		endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const updateCustomDetailsDefinitions = async (data: UpdateCustomDetailsDefinitionsDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
	});
};

export const deleteCustomDetailsDefinitions = async (data: DeleteCustomDetailsDefinitionsDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_CUSTOM_DETAILS_DEFINITIONS_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify(data),
	});
};

export const editPreFillableFormTemplate = async (data: UpdatePreFillableFormTemplateDto) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_UPDATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const deletePreFillableFormTemplate = async (templateId: string) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.GODMODE_PRE_FILLABLE_FORM_TEMPLATE_SOFT_DELETE_ID_DELETE,
			{ id: templateId }
		),
		method: HTTP_METHOD.DELETE,
	});
};

export const getAllPreFillableFormsTemplates = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<DataPreFillableFormTemplateWithProjectsTct[]>>(
		{ endpoint, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getPreFillableFormTemplateDetails = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<DataPreFillableFormTemplateWithAllRelationsTct>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getPreFillableFormOrganizationOptions = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<PreFillableFormOrganizationOptionRes[]>>(
		{ endpoint, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getPreFillableFormTemplateCustomDetailsDefinitions = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<PreFillableFormTemplateCustomDetailsDefinitionsRes>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getAllGeneratableDocumentsTemplates = async (
	endpoint: string,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<DataGeneratableDocumentTemplateWithAllRelationsTct[]>>(
		{ endpoint, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getGeneratableDocumentTemplate = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<DataGeneratableDocumentWorkflowTemplateTct>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const editGeneratableDocumentTemplate = async (
	data: UpdateGeneratableDocumentTemplateDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_UPDATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const getGeneratableDocumentTemplateAssignedProjects = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<GetGeneratableDocumentsTemplateAssignedProjectRes[]>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const getGeneratableDocumentTemplateFields = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<DataGeneratableDocumentWorkflowTemplateFieldTct[]>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const editGeneratableDocumentTemplateFields = async (
	data: UpdateGeneratableDocumentTemplateSmartFieldsDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_SMART_FIELDS_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
	});
};

export const assignProjectToGeneratableDocumentTemplate = async (
	data: AssignProjectToGeneratableDocumentTemplateDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ASSIGN_PROJECT_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': data.dealId,
		},
	});
};

export const unassignProjectFromGeneratableDocumentTemplate = async (
	data: UnassignProjectFromGeneratableDocumentTemplateDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_UNASSIGN_PROJECT_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': data.dealId,
		},
	});
};

export const getGeneratableDocumentTemplateFile = async (
	args: {
		templateType: GeneratableDocumentWorkflowTemplateType;
		version: GeneratableDocumentVersion;
		viewMode: FilePreviewMode;
	},
	signal?: AbortSignal
) => {
	if (!args.templateType || !args.version) {
		return;
	}

	const endpoint = formatUnicorn(API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_FILE_TYPE_GET, {
		type: args.templateType,
	});

	const finalEndpoint = args.version
		? `${endpoint}?version=${encodeURIComponent(args.version)}&viewMode=${encodeURIComponent(args.viewMode)}`
		: endpoint;

	return await fetchFileRequest(finalEndpoint, { abortSignal: signal });
};

export const getGdwtCustomDetailsDefinitions = async (
	endpoint: string,
	args: { id: string },
	signal?: AbortSignal
) => {
	const { id } = args;

	return await makeRequest<ApiV1.Response<GdwtCustomDetailDefinitionTct[]>>(
		{
			endpoint: formatUnicorn(endpoint, { id }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const createGdwtCustomDetailDefinition = async (
	data: CreateGdwtCustomDetailsDefinitionsDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_CUSTOM_DETAILS_DEFINITIONS_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
	});
};

export const updateGdwtCustomDetailsDefinitions = async (
	data: UpdateGdwtCustomDetailDefinitionsDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_CUSTOM_DETAILS_DEFINITIONS_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
	});
};

export const deleteGdwtCustomDetailsDefinitions = async (
	data: DeleteCustomDetailsDefinitionsDto
) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_CUSTOM_DETAILS_DEFINITIONS_DELETE,
		method: HTTP_METHOD.DELETE,
		body: JSON.stringify(data),
	});
};

export const uploadContractTemplateToGodMode = async ({
	workflowFileDto,
	mipId,
	dealId,
}: {
	workflowFileDto: DocumentWorkflowFileDto;
	mipId: string;
	dealId: string;
}) => {
	return await makeRequest<ApiV1.Response<CreateDocumentWorkflowFileRes>>({
		endpoint: API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(workflowFileDto),
		customHeaders: {
			'deal-id': dealId,
			'mip-id': mipId,
		},
	});
};

export const createSimpleDocumentWorkflowDocuments = async ({
	createDocumentWorkflowsDto,
	mipId,
	dealId,
}: {
	createDocumentWorkflowsDto: CreateDocumentWorkflowsDto;
	mipId: string;
	dealId: string;
}) => {
	return await makeRequest<ApiV1.Response<CreateDocumentWorkflowRes>>({
		endpoint: API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_CREATE_DOCUMENTS_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(createDocumentWorkflowsDto),
		customHeaders: {
			'deal-id': dealId,
			'mip-id': mipId,
		},
	});
};

export const getUserSignature = async (signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<UserSignatureRes> | undefined>(
		{ endpoint: API_PATHS.USER_SIGNATURE_GET, method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const deleteUserSignature = async () => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.USER_SIGNATURE_DELETE,
		method: HTTP_METHOD.DELETE,
	});
};

export const uploadSignatureFile = async (blob: Blob) => {
	const formData = new FormData();
	formData.append('file', blob, 'signature.png');
	return await uploadFileRequest<ApiV1.Response<DataFileTct>>(
		{ endpoint: API_PATHS.FILE_SIGNATURE_FILE_POST, method: HTTP_METHOD.POST },
		formData
	);
};

export const setUserSignature = async (signatureImageFileId: string) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: API_PATHS.USER_SIGNATURE_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({ signatureImageFileId }),
	});
};
